import { useDispatch } from 'react-redux';
import { Dispatch, SetStateAction } from 'react';
import { AssistantReservationTimeState, AssistantSummaryReservation } from '@mews-commander/data-access';
import { Button, Stack } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { navigateInBaseModal, showRouteInModal } from '../../../app/actions/common';
import { SearchStateProps } from '../../Assistant';
import ReservationCheckOutAction from '../../../reservationModule/components/SmartDetail/ReservationCheckOutAction';
import { AssistantListItemSelectionStateProps } from '../../drawer/hooks/useAssistantListItemSelectionHandler';
import { NEW_RESERVATION_BUTTON_DATA_TEST_ID } from '../../../newReservation/constants';
import { CustomerAddBill } from './CustomerAddBill';
import { AccountNoteDialog } from './CustomerAddNote';
import { CustomerClearDataDialog } from './CustomerClearData';
import { CustomerReservationCheckinContainer } from './CustomerReservationCheckinContainer';

interface ReservationRightItemProps {
    reservation: AssistantSummaryReservation | undefined;
    customerId: string;
    hasPastReservations: boolean;
    hasMoreThanOneActiveReservation: boolean;
    handleCloseSummarySidePanel: () => void;
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
}

interface CustomerQuickActionButtonProps {
    text: Mews.TextKey;
    onClick: () => void;
    variant: 'primary' | 'secondary';
    dataTestId: string;
}

const CustomerQuickActionButton = ({ text, onClick, variant, dataTestId }: CustomerQuickActionButtonProps) => (
    <Button variant={variant} size="small" onClick={onClick} data-test-id={dataTestId} type="submit">
        <LocalizedText textKey={text} />
    </Button>
);
export const ReservationPrimaryQuickAction = ({
    reservation,
    customerId,
    hasPastReservations,
    handleCloseSummarySidePanel,
    hasMoreThanOneActiveReservation,
    setSearchState,
    setListItemSelectionState,
}: ReservationRightItemProps) => {
    const enterpriseId = useCurrentEnterpriseId();
    const dispatch: CommanderThunkDispatch = useDispatch();
    const resolveRoute = useResolveRoute();
    const showCheckInButton = !hasMoreThanOneActiveReservation && reservation?.resource?.id;
    const closeSummarySidePanel = () => {
        handleCloseSummarySidePanel();
        setSearchState((prev) => ({
            ...prev,
            searchTerm: '',
            focused: false,
        }));
        setListItemSelectionState((prev) => ({
            ...prev,
            selectedIndex: 0,
            selectedTabIndex: 0,
        }));
    };

    const createNewReservation = () => {
        dispatch(
            showRouteInModal(RouteName.ReservationCreate, { query: { HotelId: enterpriseId, CustomerId: customerId } })
        );
        closeSummarySidePanel();
    };
    const viewReservations = () => {
        const url = resolveRoute(RouteName.CustomerDashboard, { id: customerId });
        dispatch(navigateInBaseModal(url));
        closeSummarySidePanel();
    };
    const viewOpenBills = () => {
        const url = resolveRoute(RouteName.BillingAccount, { accountId: customerId });
        dispatch(navigateInBaseModal(url));
        closeSummarySidePanel();
    };
    const viewProfile = () => {
        const url = resolveRoute(RouteName.CustomerDetail, { id: customerId });
        dispatch(navigateInBaseModal(url));
        closeSummarySidePanel();
    };

    if (!reservation) {
        return (
            <Stack spacing="200" alignment="end">
                <CustomerQuickActionButton
                    text={TextKey.CreateReservation}
                    onClick={createNewReservation}
                    variant="secondary"
                    dataTestId={NEW_RESERVATION_BUTTON_DATA_TEST_ID}
                />
                {customerId && hasPastReservations ? (
                    <CustomerQuickActionButton
                        text={TextKey.AssistantSummaryGuestPayInvoices}
                        onClick={viewOpenBills}
                        variant="primary"
                        dataTestId="GuestPayInvoices"
                    />
                ) : (
                    <CustomerClearDataDialog id={customerId} />
                )}
            </Stack>
        );
    }
    const renderToBeCheckedIn = () => (
        <Stack spacing="200" alignment="end">
            <AccountNoteDialog ownerId={customerId} />
            {showCheckInButton && (
                <CustomerReservationCheckinContainer
                    variant="primary"
                    reservationId={reservation.id}
                    onUpdate={() => {}}
                    withCheckInButton
                    assignedResourceId={reservation?.resource?.id}
                />
            )}
        </Stack>
    );

    const renderCheckInMissed = renderToBeCheckedIn;

    const renderCheckedIn = () => (
        <Stack spacing="200" alignment="end">
            <CustomerQuickActionButton
                text={TextKey.ViewGuest}
                onClick={viewProfile}
                variant="secondary"
                dataTestId="ViewGuest"
            />
            <CustomerAddBill key="NewBillButton" accountId={customerId} />
        </Stack>
    );

    const renderToBeCheckedOut = () => (
        <Stack spacing="200" alignment="end">
            <CustomerQuickActionButton
                text={TextKey.AssistantSummaryGuestPayOpenBills}
                onClick={viewOpenBills}
                variant="secondary"
                dataTestId="ViewOpenBills"
            />
            {!hasMoreThanOneActiveReservation && (
                <ReservationCheckOutAction reservationId={reservation.id} onUpdate={() => {}} />
            )}
        </Stack>
    );

    const renderCheckOutMissed = renderToBeCheckedOut;

    const renderFuture = () => (
        <Stack spacing="200" alignment="end">
            <AccountNoteDialog ownerId={customerId} />
            <CustomerQuickActionButton
                text={TextKey.AssistantSummaryGuestViewReservations}
                onClick={viewReservations}
                variant="primary"
                dataTestId="ViewReservations"
            />
        </Stack>
    );

    type ReservationTimeStateHandler = {
        [key in AssistantReservationTimeState]?: () => JSX.Element;
    };

    const reservationTimeStateHandlers: ReservationTimeStateHandler = {
        [AssistantReservationTimeState.ToBeCheckedIn]: renderToBeCheckedIn,
        [AssistantReservationTimeState.CheckInMissed]: renderCheckInMissed,
        [AssistantReservationTimeState.CheckedIn]: renderCheckedIn,
        [AssistantReservationTimeState.ToBeCheckedOut]: renderToBeCheckedOut,
        [AssistantReservationTimeState.CheckOutMissed]: renderCheckOutMissed,
        [AssistantReservationTimeState.Future]: renderFuture,
    };
    const handler = reservationTimeStateHandlers[reservation.timeState];
    return handler ? handler() : null;
};
